import {
  ASSET_STATUS_LABELS,
  type AuthUser,
  type DashboardServerAsset,
} from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { Link } from 'react-router';

import { assetListPath } from '@/products/helpdesk/features/assets/asset-list-path';
import {
  DashboardPanel,
  DashboardPanelLinkAction,
  DashboardPhaseHint,
  DashboardPlaceholderChart,
} from '@/products/helpdesk/features/dashboard/dashboard-panel';
import { useDashboardSummary } from '@/products/helpdesk/features/dashboard/use-dashboard-summary';
import { ApiRequestError } from '@/lib/api';

export function ServerStorageDashboardPanel({
  user: _user,
  inventarLink,
}: {
  user: AuthUser;
  inventarLink: boolean;
}) {
  void _user;
  const summary = useDashboardSummary({ enabled: inventarLink });
  const servers = inventarLink ? (summary.data?.serverAssets ?? null) : null;
  const serverTypeId = summary.data?.assets?.countsByType.find(
    (row) => row.slug === 'server',
  )?.typeId;

  return (
    <DashboardPanel
      slotId="server-storage"
      title="Server & Storage"
      description="Server aus dem Inventar und später Live-Kapazität."
      action={
        inventarLink ? (
          <DashboardPanelLinkAction
            to={serverTypeId ? assetListPath({ assetTypeId: serverTypeId }) : '/helpdesk/assets'}
            label="Inventar"
          />
        ) : undefined
      }
    >
      <div className="space-y-4">
        <div data-server-assets-slot>
          {!inventarLink ? (
            <p className="text-sm text-muted-foreground">
              Serverliste erfordert das Assets-Modul und Leserecht.
            </p>
          ) : null}

          {inventarLink && summary.isPending ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2Icon className="size-4 animate-spin" aria-hidden />
              Server werden geladen …
            </p>
          ) : null}

          {inventarLink && summary.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {summary.error instanceof ApiRequestError
                ? summary.error.message
                : 'Serverliste konnte nicht geladen werden.'}
            </p>
          ) : null}

          {inventarLink && servers?.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Keine Server-Assets im Inventar. Legen Sie Geräte vom Typ „Server“ an.
            </p>
          ) : null}

          {inventarLink && servers && servers.length > 0 ? (
            <ul className="divide-y divide-border rounded-md border">
              {servers.map((server) => (
                <ServerRow key={server.id} server={server} />
              ))}
            </ul>
          ) : null}
        </div>

        <div>
          <p className="mb-2 text-xs font-medium tracking-wide uppercase">Live-Monitoring</p>
          <DashboardPlaceholderChart label="Live-Monitoring folgt" />
          <DashboardPhaseHint>
            Live CPU/RAM/Disk & Storage-Monitoring folgt — keine Mock-Werte.
          </DashboardPhaseHint>
        </div>
      </div>
    </DashboardPanel>
  );
}

function ServerRow({ server }: { server: DashboardServerAsset }) {
  return (
    <li>
      <Link
        to={`/helpdesk/assets/${server.id}`}
        className="flex flex-col gap-0.5 px-3 py-2 hover:bg-muted/50 sm:flex-row sm:items-center sm:justify-between"
      >
        <span className="min-w-0 truncate text-sm font-medium">
          {server.name}
          {server.inventoryTag ? (
            <span className="font-normal text-muted-foreground"> · {server.inventoryTag}</span>
          ) : null}
        </span>
        <span className="text-xs text-muted-foreground">
          {ASSET_STATUS_LABELS[server.status]}
          {server.location ? ` · ${server.location}` : ''}
        </span>
      </Link>
    </li>
  );
}
